/**
 * Structural Brace Tool
 * 
 * Brace creation defined by a line.
 */

import { v4 as uuidv4 } from 'uuid';
import { BraceElement, BraceGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export class BraceTool extends BaseTool {
    id = 'brace';
    name = 'Brace';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        // Enhanced snapping: Try to snap to existing Beam/Column endpoints if possible
        // For now, grid snapping is default
        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
        } else {
            this.createBrace(this.startPoint, snapped);
            this.startPoint = null;
            this.currentPoint = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private updatePreview() {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            this.context.appStore.setToolPreview({
                type: 'brace',
                start: this.startPoint,
                end: this.currentPoint,
                width: 0.2
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createBrace(start: THREE.Vector3, end: THREE.Vector3) {
        if (!this.context) return;

        const geometryData: BraceGeometryData = {
            start: [start.x, start.y, start.z],
            end: [end.x, end.y, end.z]
        };

        const brace: BraceElement = {
            id: uuidv4(),
            category: 'Structural Framing',
            type: 'HSS Brace',
            parameters: {
                'Length': start.distanceTo(end).toFixed(2) + ' m',
                'Structural Usage': 'Vertical Bracing', // Refined usage
                'Structural Material': 'Steel',
                'Start Release': 'Pinned', // Default end condition
                'End Release': 'Pinned',   // Default end condition
                'Start Attachment Ratio': 0.0, // For connections
                'End Attachment Ratio': 1.0
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(brace);
    }
}
